import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import { CourtLines, FullCourtLines } from '@/components/CourtCanvas';
import PlayAnimation from '@/components/PlayAnimation';
import { writeWithQueue } from '@/lib/offlineSync';
import { useTeams } from '@/context/TeamContext';
import { api } from '@/lib/api';
import {
  Save, Trash2, Users, Settings, Check, Play as PlayIcon, X as XIcon,
  ArrowRightLeft, Repeat, Move, Shield, Hand, Target, CircleDot, Pencil,
  Maximize2, Minimize2, UserCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { useLang } from '@/context/LangContext';

const GOLD = '#D4AF37';
const RED  = '#E11D48';
const V = 1000;
const HOOP = { x: 50, y: 88 };
const THREE_PT_RADIUS = 47; // ~ arc radius in % court units (470/1000 = 47)

/* Visual recipe per arrow-type. Stored as `arrowType` inside paths so the
   animation viewer can render the right colour/dash without extra logic. */
const ARROW_STYLES = {
  pass:     { stroke: '#60A5FA', dash: '14 9'  },
  dribble:  { stroke: RED,       dash: '4 7'   },
  move:     { stroke: GOLD,      dash: '2 10'  },
  cut:      { stroke: GOLD,      dash: '8 4'   },
  screen:   { stroke: '#10B981', dash: 'none'  },
  block:    { stroke: '#10B981', dash: '6 6'   },
  handoff:  { stroke: '#F97316', dash: 'none'  },
  pickroll: { stroke: '#10B981', dash: 'none'  }, // first segment renders green, then red roll
  pickpop:  { stroke: '#10B981', dash: 'none'  }, // first segment green, then gold pop
  shot:     { stroke: '#A78BFA', dash: 'none'  },
  shot3:    { stroke: '#A78BFA', dash: 'none'  },
};

/* Formation templates per mode. The first entry of each list is the default
   formation used when a fresh editor opens. */
const OFFENSE_TEMPLATES = {
  '1-2-2': {
    label: '1-2-2',
    desc: '1-2-2: base equilibrada con 1 punta, 2 aleros y 2 postes.',
    spots: [[50, 22], [25, 40], [75, 40], [30, 65], [70, 65]],
  },
  'horns':  {
    label: 'Horns',
    desc: 'Horns: 2 postes en los codos creando opciones de pick & roll.',
    spots: [[50, 28], [15, 50], [85, 50], [36, 55], [64, 55]],
  },
  'box':    {
    label: 'Box',
    desc: 'Box: 4 jugadores en caja alrededor del key + 1 punta.',
    spots: [[50, 28], [32, 50], [68, 50], [32, 75], [68, 75]],
  },
  '4out':   {
    label: '4 Out',
    desc: '4 Out 1 In: 4 abiertos al perímetro + 1 poste bajo.',
    spots: [[50, 28], [18, 45], [82, 45], [22, 72], [78, 72]],
  },
};

const DEFENSE_TEMPLATES = {
  '2-3': {
    label: '2-3 Zona',
    desc: '2-3 Zona: 2 arriba + 3 abajo, protege la pintura.',
    spots: [[35, 55], [65, 55], [20, 80], [50, 78], [80, 80]],
  },
  '3-2': {
    label: '3-2 Zona',
    desc: '3-2 Zona: 3 arriba + 2 abajo, cierra el perímetro y los triples.',
    spots: [[30, 50], [50, 45], [70, 50], [30, 80], [70, 80]],
  },
  '1-3-1': {
    label: '1-3-1',
    desc: '1-3-1: presión punta, 3 medios y 1 al fondo. Trampas en esquinas.',
    spots: [[50, 35], [25, 60], [50, 58], [75, 60], [50, 82]],
  },
  'm2m':   {
    label: 'Hombre',
    desc: 'Hombre a Hombre: cada defensor sigue a un atacante.',
    spots: [[50, 30], [25, 47], [75, 47], [32, 72], [68, 72]],
  },
};

const DEFAULT_TEMPLATE = { offense: '1-2-2', defense: '2-3' };

/* Full-court templates (used when courtMode === 'full'). y values are
   absolute over the 0-100 % range of the 2000-tall viewBox — so y=92 sits at
   your baseline and y=10 is the opponent's baseline. Hand-tuned to mimic the
   classic basketball drills coaches teach. */
const FULL_OFFENSE_TEMPLATES = {
  'posicion': {
    label: 'Posición',
    desc: 'Posición 5v5: 5 ofensivos colocados en cancha completa con un defensor pegado a cada uno (auto-añade defensa).',
    spots: [[50, 88], [22, 75], [78, 75], [35, 60], [65, 60]],
    pairsDefense: true, // special: auto-toggles secondary defenders next to each attacker
  },
  'pb-1-4': {
    label: 'Press Break 1-4',
    desc: 'Press Break 1-4: ataque contra presión. 1 inbounder + 4 alineados en FT line.',
    spots: [[50, 95], [15, 82], [35, 82], [65, 82], [85, 82]],
  },
  'transit': {
    label: 'Transición',
    desc: 'Transición rápida: PG atrás, 2 wings corriendo, trailer y finisher al aro.',
    spots: [[50, 88], [22, 70], [78, 70], [50, 50], [50, 28]],
  },
  'blob': {
    label: 'Saque Fondo',
    desc: 'Saque de fondo (BLOB): inbounder + caja frente al aro tuyo.',
    spots: [[50, 96], [40, 82], [60, 82], [40, 72], [60, 72]],
  },
  '1-2-2': {
    label: '1-2-2',
    desc: '1-2-2 con vista panorámica: base equilibrada visible en cancha completa.',
    spots: [[50, 60], [25, 70], [75, 70], [30, 82], [70, 82]],
  },
};

const FULL_DEFENSE_TEMPLATES = {
  '2-2-1': {
    label: 'Press 2-2-1',
    desc: 'Press 2-2-1: 2 al frente del oponente + 2 mediocampo + 1 safety atrás.',
    spots: [[35, 25], [65, 25], [30, 55], [70, 55], [50, 90]],
  },
  '1-2-1-1': {
    label: 'Press 1-2-1-1',
    desc: 'Diamond Press: 1 punta + 2 + 1 + 1 safety. Fuerza pases laterales.',
    spots: [[50, 18], [30, 38], [70, 38], [50, 60], [50, 90]],
  },
  'fb-d': {
    label: 'Anti-transición',
    desc: 'Anti-transición: 5 defensores escalonados desde medio campo a baseline.',
    spots: [[50, 70], [25, 80], [75, 80], [50, 88], [50, 95]],
  },
  '2-3': {
    label: '2-3 Zona',
    desc: '2-3 Zona en vista completa: protege la pintura, conserva esquinas.',
    spots: [[35, 77], [65, 77], [20, 90], [50, 88], [80, 90]],
  },
};

const FULL_DEFAULT_TEMPLATE = { offense: 'pb-1-4', defense: '2-2-1' };

function getTemplateMap(mode, courtMode) {
  if (courtMode === 'full') {
    return mode === 'defense' ? FULL_DEFENSE_TEMPLATES : FULL_OFFENSE_TEMPLATES;
  }
  return mode === 'defense' ? DEFENSE_TEMPLATES : OFFENSE_TEMPLATES;
}

function getDefaultTemplate(mode, courtMode) {
  return (courtMode === 'full' ? FULL_DEFAULT_TEMPLATE : DEFAULT_TEMPLATE)[mode];
}

/**
 * Tiny preview thumbnail for a template — renders 5 dots in the formation's
 * relative positions inside a 22-px portrait box. Helps coaches (and kids)
 * recognise a formation at a glance before reading the label.
 */
function TemplateThumb({ spots, isDefense, isActive }) {
  // Maintain the 1:2 aspect ratio for full-court templates; half-court use 1:1.
  const isFull = spots.some(([, y]) => y > 50 && y < 75) && spots.length === 5
    && (Math.max(...spots.map(s => s[1])) - Math.min(...spots.map(s => s[1])) > 35);
  // Simpler heuristic: any spot with y >= 50 → portrait box that maps y∈[0,100].
  const ratio = isFull ? { w: 16, h: 22 } : { w: 22, h: 22 };
  const fill = isActive
    ? (isDefense ? '#FFFFFF' : '#0A192F')
    : (isDefense ? '#F87171' : '#D4AF37');
  return (
    <svg
      width={ratio.w}
      height={ratio.h}
      viewBox={`0 0 100 100`}
      preserveAspectRatio="none"
      className="flex-shrink-0"
      aria-hidden="true"
    >
      <rect x="0" y="0" width="100" height="100" rx="6" fill="none"
        stroke={isActive ? fill : (isDefense ? '#F87171' : '#D4AF37')}
        strokeOpacity={isActive ? 0.4 : 0.25} strokeWidth="2" />
      {spots.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="8" fill={fill} />
      ))}
    </svg>
  );
}

function makeStartersFromTemplate(mode, templateKey, courtMode = 'half') {
  const map = getTemplateMap(mode, courtMode);
  const tpl = map[templateKey] || map[getDefaultTemplate(mode, courtMode)];
  // Half-court templates need their y compressed into the bottom half when
  // displayed on a full court. Full-court-native templates already use the
  // 0-100 full range so no transform is applied.
  const isFullNative = courtMode === 'full' && (
    map === FULL_OFFENSE_TEMPLATES || map === FULL_DEFENSE_TEMPLATES
  );
  const transformY = (y) => (courtMode === 'full' && !isFullNative) ? 50 + y * 0.45 : y;
  if (mode === 'defense') {
    return tpl.spots.map(([x, y], i) => ({
      id: `d${i + 1}`, label: `X${i + 1}`, team: 'defense',
      position: { x, y: transformY(y) }, photo_url: null, player_id: null, player_name: null,
    }));
  }
  return tpl.spots.map(([x, y], i) => ({
    id: `o${i + 1}`, label: `${i + 1}`, team: 'offense',
    position: { x, y: transformY(y) }, photo_url: null, player_id: null, player_name: null,
  }));
}

/**
 * Build paired-defense tokens for a template that explicitly defines matchups
 * (e.g. the "Posición" 5v5 template). Each defender sits ~6 % above its
 * offensive counterpart, between the attacker and the basket they're heading
 * to. The function expects the *offense* spots from the template and the
 * current courtMode.
 */
function makePairedDefenders(offenseSpots, courtMode = 'full') {
  // Y offset: in full court the offense is at the bottom (y > 50) attacking
  // the top hoop → defenders sit at slightly smaller y (closer to the rim
  // they protect). In half court they sit slightly below (toward the lone
  // bottom hoop). We default to -6 because Posición only ships full-court.
  const dy = courtMode === 'full' ? -6 : -6;
  return offenseSpots.map(([x, y], i) => ({
    id: `d${i + 1}`,
    label: `X${i + 1}`,
    team: 'defense',
    position: { x, y: Math.max(0, Math.min(100, y + dy)) },
    photo_url: null,
    player_id: null,
    player_name: null,
  }));
}

/* Defenders to add as a secondary/context team when mode='offense'. */
const DEFENDER_SLOTS = [
  { x: 50, y: 35 }, { x: 25, y: 55 }, { x: 75, y: 55 },
  { x: 30, y: 78 }, { x: 70, y: 78 },
];

/* Attackers to add as a secondary/context team when mode='defense'. */
const ATTACKER_SLOTS = [
  { x: 50, y: 22 }, { x: 25, y: 38 }, { x: 75, y: 38 },
  { x: 32, y: 60 }, { x: 68, y: 60 },
];

const ATTACH_THRESHOLD = 9; // % from a player token to count as "started from this player"
const HOOP_REGION = { x: [35, 65], y: [70, 95] }; // dribble heuristic

function autoName(prefix = 'Jugada') {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${prefix} ${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/* Lightweight category detector — scans the play name for known basketball
   keywords and returns a {category, worksAgainst, label} hint. Falls back to
   the focal `mode` (offense → 'man', defense → 'zone'). */
const CATEGORY_RULES = [
  { kw: ['blob', 'saque de fondo', 'endline'],                  cat: 'blob',    label: 'BLOB' },
  { kw: ['slob', 'saque de banda', 'sideline'],                 cat: 'slob',    label: 'SLOB' },
  { kw: ['ato', 'after timeout'],                               cat: 'ato',     label: 'ATO' },
  { kw: ['endgame', 'buzzer', 'último segundo', 'last shot'],   cat: 'endgame', label: 'FINAL' },
  { kw: ['zona', 'zone', '2-3', '3-2', '1-3-1', '1-2-2 zona'],  cat: 'zone',    label: 'ZONA' },
  { kw: ['hombre', 'man-to-man', 'm2m'],                         cat: 'man',     label: 'HOMBRE' },
];

const VS_RULES = [
  { kw: ['vs zona', 'vs zone', 'vs 2-3', 'vs 3-2', 'vs 1-3-1'], against: ['zone'] },
  { kw: ['vs hombre', 'vs man', 'vs m2m'],                      against: ['man'] },
];

function detectCategory(name, mode) {
  const n = (name || '').toLowerCase();
  for (const rule of CATEGORY_RULES) {
    if (rule.kw.some(k => n.includes(k))) return { category: rule.cat, label: rule.label, auto: false };
  }
  return mode === 'defense'
    ? { category: 'zone', label: 'DEFENSA', auto: true }
    : { category: 'man',  label: 'ATAQUE',  auto: true };
}

function detectWorksAgainst(name, defaultMode) {
  const n = (name || '').toLowerCase();
  for (const rule of VS_RULES) {
    if (rule.kw.some(k => n.includes(k))) return rule.against;
  }
  return defaultMode === 'defense' ? ['man', 'zone'] : ['man'];
}

function isInHoopRegion(p) {
  return p.x >= HOOP_REGION.x[0] && p.x <= HOOP_REGION.x[1] && p.y >= HOOP_REGION.y[0] && p.y <= HOOP_REGION.y[1];
}

function nearestPlayer(point, players, excludeId = null) {
  let best = null;
  let bestD = Infinity;
  for (const p of players) {
    if (p.id === excludeId) continue;
    const d = Math.hypot(p.position.x - point.x, p.position.y - point.y);
    if (d < bestD) { bestD = d; best = p; }
  }
  return { player: best, distance: bestD };
}

export default function PlayEditorEasy() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLang();
  // Single mutable step. We persist a multi-step structure on save so the
  // viewer/animation pipeline still works, but the user never sees "steps".
  // Mode: 'offense' or 'defense'. Determines the starting formation and
  // which set of tokens the coach is designing the play for. The focal team
  // (the one in formation) is always the one whose actions the coach is
  // drawing. The opposite team can still be added as visual context.
  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem('pm_easy_mode') === 'defense' ? 'defense' : 'offense'; }
    catch { return 'offense'; }
  });
  const [courtMode, setCourtMode] = useState(() => {
    // 'half' (viewBox 1000x1000, default) | 'full' (viewBox 1000x2000)
    try { return localStorage.getItem('pm_easy_court') === 'full' ? 'full' : 'half'; }
    catch { return 'half'; }
  });
  const [template, setTemplate] = useState(() => {
    try {
      const m = localStorage.getItem('pm_easy_mode') === 'defense' ? 'defense' : 'offense';
      const cm = localStorage.getItem('pm_easy_court') === 'full' ? 'full' : 'half';
      return localStorage.getItem(`pm_easy_tpl_${m}_${cm}`) || getDefaultTemplate(m, cm);
    } catch { return getDefaultTemplate('offense', 'half'); }
  });
  const [players, setPlayers] = useState(() => makeStartersFromTemplate(mode, template, courtMode));
  const [paths, setPaths] = useState({});   // playerId -> { points, arrowType }
  // "Secondary" indicates if the opposite team is currently visible as context.
  const [hasSecondary, setHasSecondary] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [showHint, setShowHint] = useState(() => {
    try { return localStorage.getItem('pm_easy_hint_seen') !== '1'; } catch { return true; }
  });

  // Soft pulse animation: when an action is fired from the contextual menu we
  // ping the just-created path so the user gets a 600ms visual confirmation.
  // The key is bumped so React re-mounts the <g> and the CSS animation replays.
  const [pulse, setPulse] = useState(null); // { playerId, key }

  // Custom play name: by default auto-generated ("Jugada DD/MM HH:MM"). Coach
  // can override via a small ✏️ inline editor next to the Save button.
  const [customName, setCustomName] = useState('');
  const [editingName, setEditingName] = useState(false);

  // Fullscreen editor — default ON so the court fills the tablet screen as a
  // popup (sidebar hidden). Persisted in localStorage so coaches keep their
  // preferred mode. Toggleable via the Maximize / Minimize header button.
  const [fullscreen, setFullscreen] = useState(() => {
    try { return localStorage.getItem('pm_easy_fullscreen') !== '0'; }
    catch { return true; }
  });

  // ===== Roster picker (Añadir Roster) =====
  // Lets the coach replace generic 1-2-3-4-5 tokens with their actual players'
  // names + photos. Fetched lazily from the active team's roster.
  const { activeTeam } = useTeams();
  const [rosterList, setRosterList] = useState([]);   // [{ id, name, number, photo_url }]
  const [rosterLoaded, setRosterLoaded] = useState(false);
  const [rosterOpen, setRosterOpen] = useState(false);
  const [rosterPicked, setRosterPicked] = useState({}); // tokenId -> rosterPlayerId

  const loadRoster = async () => {
    try {
      const r = await api.get('/roster?status=active');
      const list = Array.isArray(r.data) ? r.data : (r.data?.roster || r.data?.players || []);
      setRosterList(list);
      setRosterLoaded(true);
    } catch {
      setRosterList([]);
      setRosterLoaded(true);
    }
  };

  const svgRef = useRef(null);
  const [drag, setDrag] = useState(null);        // { id, startPos } — currently drawing a path
  const [strokePts, setStrokePts] = useState(null); // [{x,y}] live preview points
  const [previewing, setPreviewing] = useState(false);

  // When the court is stretched to fill the screen (full-court + fullscreen +
  // `preserveAspectRatio="none"`), the x-axis ends up scaled more than the
  // y-axis on portrait tablets. That distorts every <circle> into an ellipse.
  // We measure the actual rendered SVG box and counter-stretch the player
  // tokens so they stay circular. Half-court and non-fullscreen views use
  // `meet` which preserves the aspect ratio, so tokenScaleX stays at 1.
  const [tokenScaleX, setTokenScaleX] = useState(1);

  // Contextual action menu — opens on double-tap of a player. The menu
  // floats above the token using its current screen position.
  const [actionMenu, setActionMenu] = useState(null); // { playerId, screen: {x,y} }
  const lastTapRef = useRef({ id: null, t: 0 });
  const dragStartedRef = useRef(false); // distinguishes a real drag from a tap

  // Build a 2-step synthetic play for inline animation preview:
  //   step 0 (from)  → each player at the FIRST point of its path (or current pos if no path)
  //   step 1 (to)    → current positions + the recorded paths
  const buildPreviewPlay = () => {
    const startPositions = players.map(p => {
      const path = paths[p.id];
      if (path && path.points && path.points.length > 0) {
        return { ...p, position: { x: path.points[0].x, y: path.points[0].y } };
      }
      return p;
    });
    return {
      id: 'preview',
      name: 'Preview',
      original_lang: 'es',
      description: '',
      court_mode: courtMode,
      steps: [
        { step_number: 1, description: 'Inicio',  players: startPositions, arrows: [], strokes: [], paths: {} },
        { step_number: 2, description: 'Jugada', players: players.map(p => ({ ...p })), arrows: [], strokes: [],
          paths: Object.fromEntries(
            Object.entries(paths).map(([pid, v]) => [pid, { points: v.points, speed: v.speed, delay: v.delay }]),
          ) },
      ],
    };
  };

  const dismissHint = () => {
    setShowHint(false);
    try { localStorage.setItem('pm_easy_hint_seen', '1'); } catch (_e) { /* ignore */ }
  };

  // With preserveAspectRatio="xMidYMid meet" the SVG keeps its intrinsic
  // ratio in every mode — no x/y stretch happens, so tokens stay circular
  // by default and we don't need a counter-scale.
  useEffect(() => {
    setTokenScaleX(1);
  }, [courtMode, fullscreen]);

  // ===== Pointer conversion =====
  const svgPoint = (e) => {
    const svg = svgRef.current;
    if (!svg) return { x: 50, y: 50 };
    const pt = svg.createSVGPoint();
    pt.x = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    pt.y = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
    const m = svg.getScreenCTM();
    if (!m) return { x: 50, y: 50 };
    const c = pt.matrixTransform(m.inverse());
    const Vy = courtMode === 'full' ? 2 * V : V;
    return {
      x: Math.max(0, Math.min(100, (c.x / V) * 100)),
      y: Math.max(0, Math.min(100, (c.y / Vy) * 100)),
    };
  };

  // ===== Gesture: drag from a player =====
  const onPlayerDown = (pid) => (e) => {
    e.stopPropagation();
    const me = players.find(p => p.id === pid);
    if (!me) return;

    // Detect double-tap: if the same player was tapped within 400ms AND the
    // previous interaction did NOT become a drag, open the action menu.
    const now = Date.now();
    const prev = lastTapRef.current;
    if (prev.id === pid && (now - prev.t) < 400 && !prev.dragged) {
      // Compute the on-screen position of the player to anchor the menu.
      const rect = svgRef.current?.getBoundingClientRect();
      if (rect) {
        const sx = rect.left + (me.position.x / 100) * rect.width;
        const sy = rect.top + (me.position.y / 100) * rect.height;
        setActionMenu({ playerId: pid, screen: { x: sx, y: sy } });
      }
      lastTapRef.current = { id: null, t: 0, dragged: false };
      return;
    }
    lastTapRef.current = { id: pid, t: now, dragged: false };
    dragStartedRef.current = false;

    setDrag({ id: pid, startPos: { x: me.position.x, y: me.position.y } });
    setStrokePts([{ x: me.position.x, y: me.position.y }]);
    svgRef.current?.setPointerCapture?.(e.pointerId);
    if (showHint) dismissHint();
  };

  const onCanvasMove = (e) => {
    if (!drag) return;
    const pt = svgPoint(e);
    setStrokePts(pts => {
      const list = pts || [];
      // Down-sample: skip points closer than 0.5% from the last one to keep
      // the saved payload compact and the animation playback smooth.
      const last = list[list.length - 1];
      if (last && Math.hypot(pt.x - last.x, pt.y - last.y) < 0.5) return list;
      if (list.length > 1) {
        dragStartedRef.current = true;
        // Any genuine drag immediately invalidates a pending double-tap so a
        // slow finger drag can never accidentally open the action menu.
        lastTapRef.current = { id: null, t: 0, dragged: true };
      }
      return [...list, pt];
    });
  };

  const onCanvasUp = (e) => {
    if (!drag) return;
    const pts = strokePts || [];
    setStrokePts(null);
    setDrag(null);

    // Path needs at least a small distance to count — otherwise it's a tap
    if (pts.length < 2) return;
    const start = pts[0];
    const end = pts[pts.length - 1];
    const totalDist = Math.hypot(end.x - start.x, end.y - start.y);
    if (totalDist < 3) return; // accidental micro-drag

    // This was a real drag → consume any pending double-tap detection
    lastTapRef.current = { ...lastTapRef.current, dragged: true };

    // Auto-classify the gesture by analysing the endpoint
    const { player: targetPlayer, distance } = nearestPlayer(end, players, drag.id);
    let arrowType = 'move';
    if (targetPlayer && distance < ATTACH_THRESHOLD) {
      // Ended near another player → pass
      arrowType = 'pass';
    } else if (isInHoopRegion(end)) {
      // Ended near the hoop → dribble
      arrowType = 'dribble';
    }

    // Commit: register the motion path + update the player's final position
    setPaths(prev => ({ ...prev, [drag.id]: { points: pts, speed: 1.0, delay: 0, arrowType } }));
    setPlayers(prev => prev.map(pl => pl.id === drag.id ? { ...pl, position: end } : pl));
    setPulse({ playerId: drag.id, key: Date.now() });

    const label = { pass: t('ee_action_pass'), dribble: t('ee_action_dribble'), move: t('ee_action_move') }[arrowType];
    toast.success(`${label} de #${players.find(p => p.id === drag.id)?.label}`, { duration: 1400 });
  };

  // ===== Mode / Big buttons =====
  const switchMode = (newMode) => {
    if (newMode === mode) return;
    setMode(newMode);
    const newTpl = (() => {
      try { return localStorage.getItem(`pm_easy_tpl_${newMode}_${courtMode}`) || getDefaultTemplate(newMode, courtMode); }
      catch { return getDefaultTemplate(newMode, courtMode); }
    })();
    setTemplate(newTpl);
    try { localStorage.setItem('pm_easy_mode', newMode); } catch (_) { /* ignore */ }
    setPlayers(makeStartersFromTemplate(newMode, newTpl, courtMode));
    setPaths({});
    setHasSecondary(false);
    setSavedId(null);
    toast.info(newMode === 'defense' ? t('ee_toast_defense_on') : t('ee_toast_offense_on'));
  };

  const switchTemplate = (key) => {
    if (key === template) return;
    setTemplate(key);
    try { localStorage.setItem(`pm_easy_tpl_${mode}_${courtMode}`, key); } catch (_) { /* ignore */ }
    const map = getTemplateMap(mode, courtMode);
    const tpl = map[key];
    const starters = makeStartersFromTemplate(mode, key, courtMode);
    if (tpl?.pairsDefense && mode === 'offense') {
      // Posición-style template → place defenders next to each attacker.
      const defenders = makePairedDefenders(tpl.spots, courtMode);
      setPlayers([...starters, ...defenders]);
      setHasSecondary(true);
    } else {
      setPlayers(starters);
      setHasSecondary(false);
    }
    setPaths({});
    setSavedId(null);
  };

  const switchCourtMode = (newCM) => {
    if (newCM === courtMode) return;
    setCourtMode(newCM);
    try { localStorage.setItem('pm_easy_court', newCM); } catch (_) { /* ignore */ }
    // Switching court mode → pick whatever template was last used in that mode
    // (or its default). Keeps muscle memory intact.
    const newTpl = (() => {
      try { return localStorage.getItem(`pm_easy_tpl_${mode}_${newCM}`) || getDefaultTemplate(mode, newCM); }
      catch { return getDefaultTemplate(mode, newCM); }
    })();
    setTemplate(newTpl);
    setPlayers(makeStartersFromTemplate(mode, newTpl, newCM));
    setPaths({});
    setHasSecondary(false);
    setSavedId(null);
    toast.info(newCM === 'full' ? t('ee_toast_full_court') : t('ee_toast_half_court'));
  };

  const clearAll = () => {
    setPaths({});
    setPlayers(makeStartersFromTemplate(mode, template, courtMode));
    setHasSecondary(false);
    setSavedId(null);
    setRosterPicked({});
    toast.info(t('ee_toast_clean'));
  };

  // ===== Roster assignment (Añadir Roster) =====
  // Replaces a generic numbered token (e.g. "1") with the chosen roster
  // player's identifier so saved plays carry the real player_id / player_name
  // and the on-court token shows their jersey number or initials.
  const assignRoster = (slotId, rosterPlayerId) => {
    const rp = rosterList.find(r => r.id === rosterPlayerId);
    setRosterPicked(prev => {
      const next = { ...prev };
      if (rosterPlayerId) next[slotId] = rosterPlayerId; else delete next[slotId];
      return next;
    });
    setPlayers(prev => prev.map(pl => {
      if (pl.id !== slotId) return pl;
      if (!rp) {
        // Unassigned — revert to generic label
        const isOffense = pl.team === 'offense';
        const num = pl.id.replace(/^[oda]/, '');
        return { ...pl, label: isOffense ? num : `X${num}`, player_id: null, player_name: null, photo_url: null };
      }
      const shortLabel = rp.number != null && rp.number !== ''
        ? String(rp.number)
        : (rp.name || '').slice(0, 2).toUpperCase();
      return { ...pl, label: shortLabel, player_id: rp.id, player_name: rp.name, photo_url: rp.photo_url || null };
    }));
  };

  const focalPlayers = useMemo(() => players.filter(p => p.team === mode), [players, mode]);

  const openRoster = async () => {
    if (!rosterLoaded) await loadRoster();
    setRosterOpen(true);
  };

  const toggleFullscreen = () => {
    setFullscreen(v => {
      const next = !v;
      try { localStorage.setItem('pm_easy_fullscreen', next ? '1' : '0'); } catch (_e) { /* ignore */ }
      return next;
    });
  };

  // Toggles the OPPOSITE team as visual context. In offense mode, this adds
  // 5 defenders. In defense mode, this adds 5 attackers.
  const toggleSecondary = () => {
    const oppositeTeam = mode === 'offense' ? 'defense' : 'offense';
    if (hasSecondary) {
      setPlayers(prev => prev.filter(p => p.team !== oppositeTeam));
      setPaths(prev => {
        const next = {};
        const survivorIds = new Set(players.filter(p => p.team !== oppositeTeam).map(p => p.id));
        for (const [k, v] of Object.entries(prev)) if (survivorIds.has(k)) next[k] = v;
        return next;
      });
      setHasSecondary(false);
    } else {
      const slots = mode === 'offense' ? DEFENDER_SLOTS : ATTACKER_SLOTS;
      const labelPrefix = mode === 'offense' ? 'X' : '';
      const idPrefix = mode === 'offense' ? 'd' : 'a';
      const newPs = slots.map((slot, i) => ({
        id: `${idPrefix}${i + 1}`,
        label: `${labelPrefix}${i + 1}`,
        team: oppositeTeam,
        position: { ...slot },
        photo_url: null,
        player_id: null,
        player_name: null,
      }));
      setPlayers(prev => [...prev, ...newPs]);
      setHasSecondary(true);
    }
  };

  // ===== Contextual action menu =====
  //
  // Each action auto-resolves a target based on the current state and
  // generates a path/arrow with no further user input. The path is stored in
  // the same shape that the freehand gesture uses, so PlayAnimation and the
  // save endpoint don't need any changes.
  const commitPath = (playerId, points, arrowType, opts = {}) => {
    setPaths(prev => ({
      ...prev,
      [playerId]: { points, speed: 1.0, delay: 0, arrowType, ...opts },
    }));
    // Update the player's "final" position to the last point of the path so
    // subsequent gestures start from the new location.
    const last = points[points.length - 1];
    setPlayers(prev => prev.map(pl => pl.id === playerId ? { ...pl, position: { x: last.x, y: last.y } } : pl));
  };

  const closeMenu = () => setActionMenu(null);

  const distToHoop = (pos) => Math.hypot(pos.x - HOOP.x, pos.y - HOOP.y);
  const isOutside3pt = (pos) => distToHoop(pos) > THREE_PT_RADIUS;

  const runAction = (actionId) => {
    const me = actionMenu && players.find(p => p.id === actionMenu.playerId);
    if (!me) return;
    // "Teammates" = same-team players (depends on focal mode). "Opponents" =
    // the other team. This keeps Pase/Pantalla/etc. relative to the focal team.
    const teammates = players.filter(p => p.team === me.team && p.id !== me.id);
    const opponents = players.filter(p => p.team !== me.team);

    const ok = (label) => toast.success(`${label} de #${me.label}`, { duration: 1400 });
    const fail = (msg) => toast.error(msg);

    // Helper: pulse the just-committed action for visual feedback.
    const firePulse = () => setPulse({ playerId: me.id, key: Date.now() });

    switch (actionId) {
      case 'pase': {
        if (!teammates.length) return fail(t('ee_toast_no_teammates'));
        const { player: mate } = nearestPlayer(me.position, teammates);
        // Pass = the ball travels, the passer stays put. We model it as a
        // path on the passer pointing to the receiver (single arrow), without
        // moving the player.
        setPaths(prev => ({ ...prev, [me.id]: { points: [me.position, mate.position], speed: 1.0, delay: 0, arrowType: 'pass', noMove: true } }));
        ok(t('ee_action_pass'));
        break;
      }
      case 'drible': {
        const target = { x: me.position.x + (HOOP.x - me.position.x) * 0.5, y: me.position.y + (HOOP.y - me.position.y) * 0.5 };
        commitPath(me.id, [me.position, target], 'dribble');
        ok(t('ee_action_dribble'));
        break;
      }
      case 'corte': {
        // Cut toward the elbow nearest the player → creates an opening.
        const elbows = [{ x: 34, y: 60 }, { x: 66, y: 60 }];
        const target = elbows.reduce((a, b) => Math.hypot(a.x - me.position.x, a.y - me.position.y) < Math.hypot(b.x - me.position.x, b.y - me.position.y) ? a : b);
        commitPath(me.id, [me.position, target], 'cut');
        ok(t('ee_action_cut'));
        break;
      }
      case 'pantalla': {
        if (!teammates.length) return fail(t('ee_toast_no_blockers'));
        const { player: mate } = nearestPlayer(me.position, teammates);
        // Move next to the teammate to set the screen (slight offset).
        const target = { x: mate.position.x + (me.position.x > mate.position.x ? -6 : 6), y: mate.position.y - 4 };
        commitPath(me.id, [me.position, target], 'screen');
        ok(t('ee_action_screen'));
        break;
      }
      case 'bloqueo': {
        if (!opponents.length) {
          fail(me.team === 'offense' ? t('ee_toast_add_defense_first') : t('ee_toast_add_offense_first'));
          break;
        }
        const { player: d } = nearestPlayer(me.position, opponents);
        const target = { x: d.position.x, y: d.position.y - 4 };
        commitPath(me.id, [me.position, target], 'block');
        ok(t('ee_action_block'));
        break;
      }
      case 'handoff': {
        if (!teammates.length) return fail(t('ee_toast_no_teammates'));
        const { player: mate } = nearestPlayer(me.position, teammates);
        // Handoff: passer and receiver meet halfway.
        const meet = { x: (me.position.x + mate.position.x) / 2, y: (me.position.y + mate.position.y) / 2 };
        commitPath(me.id, [me.position, meet], 'handoff');
        ok(t('ee_action_handoff'));
        break;
      }
      case 'pickroll':
      case 'pickpop': {
        if (!teammates.length) return fail(t('ee_toast_no_teammates'));
        const { player: mate } = nearestPlayer(me.position, teammates);
        const screenSpot = { x: mate.position.x + (me.position.x > mate.position.x ? -6 : 6), y: mate.position.y - 4 };
        const second = actionId === 'pickroll'
          ? HOOP                                                                         // roll
          : { x: me.position.x < 50 ? 18 : 82, y: 55 };                                   // pop to wing-ish
        // Two-segment path: screener → screenSpot → secondary destination.
        commitPath(me.id, [me.position, screenSpot, second], actionId);
        ok(actionId === 'pickroll' ? 'Pick & Roll' : 'Pick & Pop');
        break;
      }
      case 'tiro': {
        // Shot doesn't move the player — only draws an arrow toward the hoop.
        setPaths(prev => ({ ...prev, [me.id]: { points: [me.position, HOOP], speed: 1.0, delay: 0, arrowType: 'shot', noMove: true } }));
        ok(t('ee_action_shot'));
        break;
      }
      case 'tiro3': {
        if (!isOutside3pt(me.position)) {
          fail(t('ee_toast_move_outside_arc'));
          break;
        }
        setPaths(prev => ({ ...prev, [me.id]: { points: [me.position, HOOP], speed: 1.0, delay: 0, arrowType: 'shot3', noMove: true } }));
        ok(`${t('ee_action_shot')} 3`);
        break;
      }
      default: break;
    }
    firePulse();
    closeMenu();
  };

  const save = async () => {
    setSaving(true);
    try {
      // Build a single-step payload — fully compatible with the existing
      // viewer / animation pipeline that reads play.steps[*].players / paths.
      const stepPayload = {
        step_number: 1,
        description: 'Paso 1',
        players: players.map(p => ({ ...p })),
        arrows: [],
        strokes: [],
        paths: Object.fromEntries(
          Object.entries(paths).map(([pid, v]) => [pid, { points: v.points, speed: v.speed, delay: v.delay, arrowType: v.arrowType }]),
        ),
      };
      const playName = (customName.trim() || autoName(t('ee_default_play_name')));
      const cat = detectCategory(playName, mode);
      const against = detectWorksAgainst(playName, mode);
      const payload = {
        name: playName,
        category: cat.category,
        difficulty: 'beginner',
        description: mode === 'defense' ? t('ee_play_defensive') : '',
        works_against: against,
        court_mode: courtMode,
        steps: [stepPayload],
      };
      const r = await writeWithQueue({ method: 'post', path: '/plays', data: payload });
      if (r.offline) {
        toast.success(`${t('ee_btn_saved')} (offline)`);
      } else {
        setSavedId(r.data.id);
        toast.success('¡Jugada guardada!');
      }
    } catch {
      toast.error(t('ee_toast_save_failed'));
    } finally {
      setSaving(false);
    }
  };

  // ===== Rendering helpers =====
  const onCourtDownNoop = (e) => {
    // Tapping empty court does nothing — only player-drag creates paths.
    e.stopPropagation();
  };

  // The y-dimension of the SVG viewBox changes with the courtMode. We use Vy
  // everywhere we map % → svg-y to keep the maths in one place.
  const Vy = courtMode === 'full' ? 2 * V : V;

  // Fullscreen wraps the editor in a popup-style overlay (no sidebar) so the
  // court fills the whole tablet/desktop viewport. Embedded mode keeps the
  // sidebar layout for navigation between sections.
  const inner = (
    <>
      <style>{`
        @keyframes pmEasyDraw {
          from { stroke-dashoffset: 1200; opacity: 0.4; }
          to   { stroke-dashoffset: 0;    opacity: 0.92; }
        }
        @keyframes pmEasyBounce {
          0%   { transform: scale(1); }
          50%  { transform: scale(1.18); }
          100% { transform: scale(1); }
        }
        .pm-pulse-path  { stroke-dasharray: 1200; animation: pmEasyDraw 600ms ease-out both; }
        .pm-pulse-token { animation: pmEasyBounce 400ms ease-out; transform-origin: center; transform-box: fill-box; }
        @media (prefers-reduced-motion: reduce) {
          .pm-pulse-path  { animation: none !important; stroke-dashoffset: 0; }
          .pm-pulse-token { animation: none !important; }
        }
      `}</style>

      {/* Compact horizontal header — fits on one row on tablets. The big
          "Crea tu jugada" title is dropped in fullscreen to free vertical
          space (the user already knows they're in the editor). */}
      <div className="flex items-center justify-between gap-2 mb-2 flex-nowrap">
        <div className="flex items-center gap-2 min-w-0">
          {!fullscreen && (
            <h1 className="font-h uppercase text-xl sm:text-2xl truncate" title="Crea tu jugada">
              Crea tu jugada
            </h1>
          )}
          {/* Court-mode toggle moved here so the header is compact */}
          <div className="inline-flex rounded-full border-2 border-gold/30 overflow-hidden text-[11px] font-h uppercase tracking-wider flex-shrink-0" data-testid="editor-easy-court-toggle">
            <button
              onClick={() => switchCourtMode('half')}
              data-testid="editor-easy-court-half"
              className={`px-3 py-1.5 transition ${courtMode === 'half' ? 'bg-gold text-navy font-semibold' : 'text-gold/70 hover:text-gold hover:bg-gold/5'}`}
            >
              {t('ee_court_half')}
            </button>
            <button
              onClick={() => switchCourtMode('full')}
              data-testid="editor-easy-court-full"
              className={`px-3 py-1.5 transition ${courtMode === 'full' ? 'bg-gold text-navy font-semibold' : 'text-gold/70 hover:text-gold hover:bg-gold/5'}`}
            >
              {t('ee_court_full')}
            </button>
          </div>
          {/* Offense / Defense mode toggle */}
          <div className="inline-flex rounded-full border-2 border-gold/40 overflow-hidden text-[11px] font-h uppercase tracking-wider flex-shrink-0" data-testid="editor-easy-mode-toggle">
            <button
              onClick={() => switchMode('offense')}
              data-testid="editor-easy-mode-offense"
              className={`px-3 py-1.5 transition ${mode === 'offense' ? 'bg-gold text-navy font-semibold' : 'text-gold/70 hover:text-gold hover:bg-gold/5'}`}
            >
              {t('ee_team_offense')}
            </button>
            <button
              onClick={() => switchMode('defense')}
              data-testid="editor-easy-mode-defense"
              className={`px-3 py-1.5 transition ${mode === 'defense' ? 'bg-red-500 text-white font-semibold' : 'text-red-400/80 hover:text-red-300 hover:bg-red-500/5'}`}
            >
              {t('ee_team_defense')}
            </button>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={toggleFullscreen}
            data-testid="editor-easy-fullscreen-toggle"
            title={fullscreen ? t('ee_tooltip_exit_fullscreen') : t('ee_tooltip_fullscreen')}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-gold/30 text-gold/80 hover:text-gold hover:bg-gold/5 text-[11px] font-h uppercase tracking-wider"
          >
            {fullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </button>
          <button
            onClick={() => navigate('/editor?pro=1')}
            data-testid="editor-easy-advanced"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-gold/30 text-gold/80 hover:text-gold hover:bg-gold/5 text-[11px] font-h uppercase tracking-wider"
            title={t('ee_tooltip_advanced_mode')}
          >
            <Settings className="w-3.5 h-3.5" />
          </button>
          {fullscreen && (
            <button
              onClick={() => navigate('/playbook')}
              data-testid="editor-easy-close"
              title={t('ee_tooltip_exit_editor')}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-red-400/40 text-red-300 hover:text-red-100 hover:bg-red-500/10 text-[11px] font-h uppercase tracking-wider"
            >
              <XIcon className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Editable play-name chip + auto-category badge — sits above the
          court card so it doesn't overlap the template ribbon. */}
      {(() => {
        const livePreview = customName.trim() || autoName(t('ee_default_play_name'));
        const detected = detectCategory(livePreview, mode);
        return (
          <div className="flex items-center gap-2 mb-2 flex-wrap" data-testid="editor-easy-name-chip">
            {editingName ? (
              <input
                autoFocus
                type="text"
                value={customName}
                onChange={e => setCustomName(e.target.value.slice(0, 60))}
                onBlur={() => setEditingName(false)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') setEditingName(false); }}
                placeholder={autoName(t('ee_default_play_name'))}
                data-testid="editor-easy-name-input"
                className="bg-navy-deep border-2 border-gold rounded-full px-3 py-1.5 text-sm font-h uppercase tracking-wider text-white placeholder:text-gray-500 outline-none"
                style={{ minWidth: 240 }}
              />
            ) : (
              <button
                onClick={() => setEditingName(true)}
                data-testid="editor-easy-name-edit"
                className="inline-flex items-center gap-1.5 bg-card-steel border border-gold/40 hover:border-gold rounded-full px-3 py-1.5 text-sm font-h uppercase tracking-wider text-gold hover:bg-gold/10 transition"
                title={t('ee_tooltip_edit_name')}
              >
                <Pencil className="w-3 h-3" />
                <span className="truncate max-w-[260px]">{livePreview}</span>
              </button>
            )}
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest border ${
                detected.auto
                  ? 'border-gold/30 text-gold/70 bg-navy-deep/70'
                  : 'border-gold text-navy bg-gold'
              }`}
              data-testid="editor-easy-name-badge"
              title={detected.auto ? t('ee_tooltip_category_default') : t('ee_tooltip_category_detected')}
            >
              {detected.label === 'DEFENSA' ? t('ee_label_defense') : detected.label === 'ATAQUE' ? t('ee_label_attack') : detected.label}
            </span>
          </div>
        );
      })()}

      <div
        className={`relative rounded-lg overflow-hidden border-2 border-gold/60 bg-navy-deep ${fullscreen ? 'flex-1 min-h-0 flex flex-col' : ''}`}
        style={{ touchAction: 'none' }}
      >
        {/* Template selector ribbon — small pills above the court */}
        {!previewing && (
          <div className="flex items-center gap-1.5 px-3 py-2 bg-navy-deep/95 border-b border-gold/20 flex-wrap" data-testid="editor-easy-templates">
            <span className="text-[10px] font-mono uppercase tracking-widest text-gold/70 me-1">Plantilla:</span>
            {Object.entries(getTemplateMap(mode, courtMode)).map(([key, tpl]) => (
              <button
                key={key}
                onClick={() => switchTemplate(key)}
                data-testid={`easy-tpl-${key}`}
                title={tpl.desc || tpl.label}
                className={`pl-1.5 pr-2.5 py-1 rounded-full text-[11px] font-h uppercase tracking-wider transition inline-flex items-center gap-1.5 ${
                  template === key
                    ? (mode === 'defense' ? 'bg-red-500 text-white' : 'bg-gold text-navy')
                    : 'text-gray-400 hover:text-white border border-steel hover:border-gold/40'
                }`}
              >
                <TemplateThumb spots={tpl.spots} isDefense={mode === 'defense'} isActive={template === key} />
                {tpl.label}
              </button>
            ))}
          </div>
        )}
        {previewing ? (
          <div data-testid="editor-easy-preview" className={fullscreen ? 'flex-1 min-h-0 relative' : ''}>
            <PlayAnimation play={buildPreviewPlay()} rosterMap={{}} autoplay courtMode={courtMode} />
            <button
              onClick={() => setPreviewing(false)}
              data-testid="editor-easy-preview-stop"
              className="absolute top-3 right-3 z-10 px-3 py-2 rounded-full bg-red-500 text-white text-xs font-h uppercase tracking-wider shadow-2xl inline-flex items-center gap-1"
            >
              <XIcon className="w-3.5 h-3.5" /> Cerrar preview
            </button>
          </div>
        ) : (
        <svg
          ref={svgRef}
          viewBox={courtMode === 'full' ? `0 0 ${V} ${2 * V}` : `0 0 ${V} ${V}`}
          className={`${fullscreen ? 'flex-1 min-h-0 w-full' : 'w-full h-auto'} block touch-none select-none mx-auto`}
          preserveAspectRatio={courtMode === 'full' ? 'none' : 'xMidYMid meet'}
          onPointerDown={onCourtDownNoop}
          onPointerMove={onCanvasMove}
          onPointerUp={onCanvasUp}
          onPointerCancel={() => { setDrag(null); setStrokePts(null); }}
          style={{
            touchAction: 'none',
            background: 'radial-gradient(ellipse at center top, #0E2440, #0A192F 70%)',
            // In fullscreen the flex parent already constrains height. In
            // non-fullscreen we cap to viewport so the toolbar stays visible.
            maxHeight: fullscreen ? undefined : (courtMode === 'full' ? 'calc(100vh - 240px)' : undefined),
          }}
          data-testid="editor-easy-svg"
        >
          {courtMode === 'full' ? <FullCourtLines /> : <CourtLines />}

          {/* Committed paths */}
          {Object.entries(paths).map(([pid, v]) => {
            const pts = v.points;
            if (!pts || pts.length < 2) return null;
            const style = ARROW_STYLES[v.arrowType] || ARROW_STYLES.move;
            const last = pts[pts.length - 1];
            const prev = pts[pts.length - 2];
            // Arrow head as a small triangle pointing along the last segment
            const dx = last.x - prev.x, dy = last.y - prev.y;
            const ang = Math.atan2(dy, dx);
            const ax = (last.x / 100) * V;
            const ay = (last.y / 100) * Vy;
            const head = 18;
            const a1x = ax - Math.cos(ang) * head + Math.cos(ang + 2.5) * head * 0.6;
            const a1y = ay - Math.sin(ang) * head + Math.sin(ang + 2.5) * head * 0.6;
            const a2x = ax - Math.cos(ang) * head + Math.cos(ang - 2.5) * head * 0.6;
            const a2y = ay - Math.sin(ang) * head + Math.sin(ang - 2.5) * head * 0.6;

            // Pick & Roll / Pop render the second segment in the destination
            // colour (red for roll, gold for pop) to communicate the play type.
            const isTwoSegment = (v.arrowType === 'pickroll' || v.arrowType === 'pickpop') && pts.length >= 3;
            const segB = isTwoSegment ? pts.slice(1) : null;
            const segBStroke = v.arrowType === 'pickroll' ? RED : GOLD;
            const segBDash = v.arrowType === 'pickroll' ? '4 7' : '2 10';

            const isPulsing = pulse?.playerId === pid;
            const pulseKey = isPulsing ? pulse.key : 'static';
            const pulseClass = isPulsing ? 'pm-pulse-path' : '';

            return (
              <g key={`${pid}-${pulseKey}`}>
                <polyline
                  className={pulseClass}
                  points={(isTwoSegment ? pts.slice(0, 2) : pts).map(p => `${(p.x / 100) * V},${(p.y / 100) * Vy}`).join(' ')}
                  fill="none"
                  stroke={style.stroke}
                  strokeWidth="6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeDasharray={style.dash}
                  opacity="0.92"
                  vectorEffect="non-scaling-stroke"
                />
                {segB && (
                  <polyline
                    className={pulseClass}
                    points={segB.map(p => `${(p.x / 100) * V},${(p.y / 100) * Vy}`).join(' ')}
                    fill="none"
                    stroke={segBStroke}
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeDasharray={segBDash}
                    opacity="0.92"
                    vectorEffect="non-scaling-stroke"
                  />
                )}
                {/* Arrow head — counter-scale so the triangle stays the same
                    shape whether the court is square (half) or stretched
                    (full). Polygon points are now relative to the head
                    origin (ax,ay) so the wrapping transform handles both
                    placement and shape correction. */}
                <g transform={`translate(${ax},${ay}) scale(${tokenScaleX}, 1)`}>
                  <polygon
                    points={`0,0 ${a1x - ax},${a1y - ay} ${a2x - ax},${a2y - ay}`}
                    fill={isTwoSegment ? segBStroke : style.stroke}
                  />
                </g>
                {/* Tiro de 3 badge */}
                {v.arrowType === 'shot3' && (
                  <g transform={`translate(${ax - 30},${ay - 30}) scale(${tokenScaleX}, 1)`}>
                    <circle r="14" fill="#A78BFA" />
                    <text textAnchor="middle" dy="5" fontFamily="Oswald" fontWeight="700" fontSize="16" fill="#0A192F">3</text>
                  </g>
                )}
              </g>
            );
          })}

          {/* Live stroke preview */}
          {strokePts && strokePts.length > 1 && (
            <polyline
              points={strokePts.map(p => `${(p.x / 100) * V},${(p.y / 100) * Vy}`).join(' ')}
              fill="none" stroke={GOLD} strokeWidth="6"
              strokeLinecap="round" strokeLinejoin="round" opacity="0.7"
              vectorEffect="non-scaling-stroke"
            />
          )}

          {/* Player tokens — big, finger-friendly */}
          {players.map(p => {
            const x = (p.position.x / 100) * V;
            const y = (p.position.y / 100) * Vy;
            const stroke = p.team === 'offense' ? GOLD : RED;
            const isPulsing = pulse?.playerId === p.id;
            const photo = p.photo_url;
            const clipId = `pmEasyClip-${p.id}`;
            return (
              <g
                key={`${p.id}-${isPulsing ? pulse.key : 'static'}`}
                transform={`translate(${x},${y}) scale(${tokenScaleX}, 1)`}
                onPointerDown={onPlayerDown(p.id)}
                style={{ cursor: 'grab', touchAction: 'none' }}
                data-testid={`easy-player-${p.id}`}
              >
                <g className={isPulsing ? 'pm-pulse-token' : ''}>
                  {photo ? (
                    <>
                      <defs>
                        <clipPath id={clipId}>
                          <circle r="44" />
                        </clipPath>
                      </defs>
                      <circle r="48" fill="#0A192F" stroke={stroke} strokeWidth="4" />
                      <image
                        href={photo}
                        x={-44}
                        y={-44}
                        width="88"
                        height="88"
                        preserveAspectRatio="xMidYMid slice"
                        clipPath={`url(#${clipId})`}
                      />
                      {/* Jersey number / label badge in the bottom-right corner */}
                      <g transform="translate(32,30)">
                        <circle r="16" fill={stroke} />
                        <text textAnchor="middle" dy="5" fontFamily="Oswald" fontWeight="700" fontSize="18" fill="#0A192F">
                          {p.label}
                        </text>
                      </g>
                    </>
                  ) : (
                    <>
                      <circle r="48" fill="#0A192F" stroke={stroke} strokeWidth="4" />
                      <circle r="48" fill="transparent" stroke={stroke} strokeWidth="1" opacity="0.4" />
                      <text textAnchor="middle" dy="12" fontFamily="Oswald" fontWeight="700" fontSize="38" fill={stroke}>
                        {p.label}
                      </text>
                    </>
                  )}
                </g>
              </g>
            );
          })}

          {/* Onboarding hint — disappears on first gesture. We center the
              banner and counter-scale horizontally so the text and rect
              stay readable on a stretched full court. */}
          {showHint && (
            <g pointerEvents="none" transform={`translate(${V / 2}, ${V * 0.04}) scale(${tokenScaleX}, 1)`}>
              <rect x={-V * 0.32} y={0} width={V * 0.64} height={70} rx={12}
                fill="#0A192F" stroke={GOLD} strokeWidth="2" opacity="0.96" vectorEffect="non-scaling-stroke" />
              <text x={0} y={32} textAnchor="middle"
                fontFamily="IBM Plex Sans" fontSize="22" fontWeight="600" fill="#fff">
                {t('ee_draw_instruction')}
              </text>
              <text x={0} y={56} textAnchor="middle"
                fontFamily="IBM Plex Sans" fontSize="14" fill={GOLD}>
                Si llegás a otro jugador → pase · si llegás al aro → drible
              </text>
            </g>
          )}
        </svg>
        )}
      </div>

      {/* Bottom action bar — sits below the court card in normal flow so the
          Save button is never "below" or hidden. Horizontal nowrap (with
          shrink) keeps all 4 buttons on a single row on tablets. */}
      {!previewing && (
        <div
          className="mt-2 flex items-center justify-center gap-2 flex-nowrap overflow-x-auto px-2"
          data-testid="editor-easy-toolbar"
        >
          <button
            onClick={toggleSecondary}
            data-testid="editor-easy-defense"
            className={`py-2.5 px-3 rounded-full font-h uppercase tracking-wider text-[11px] sm:text-xs font-semibold border-2 transition active:scale-95 whitespace-nowrap flex-shrink-0 ${
              hasSecondary
                ? (mode === 'offense' ? 'bg-red-500/20 border-red-400 text-red-100' : 'bg-gold/20 border-gold text-gold')
                : (mode === 'offense' ? 'bg-navy-deep border-red-500/70 text-red-300' : 'bg-navy-deep border-gold/70 text-gold')
            }`}
          >
            <Users className="w-4 h-4 inline me-1" />
            {hasSecondary
              ? (mode === 'offense' ? t('ee_btn_remove_defense') : t('ee_btn_remove_offense'))
              : (mode === 'offense' ? t('ee_btn_add_defense') : t('ee_btn_add_offense'))}
          </button>
          <button
            onClick={openRoster}
            data-testid="editor-easy-roster-btn"
            title="Asignar jugadores reales de tu roster"
            className="py-2.5 px-3 rounded-full font-h uppercase tracking-wider text-[11px] sm:text-xs font-semibold border-2 border-gold/60 bg-navy-deep text-gold active:scale-95 whitespace-nowrap flex-shrink-0"
          >
            <UserCheck className="w-4 h-4 inline me-1" /> {t('nav_roster')}
          </button>
          {Object.keys(paths).length > 0 && (
            <button
              onClick={() => setPreviewing(true)}
              data-testid="editor-easy-preview-btn"
              className="py-2.5 px-3 rounded-full font-h uppercase tracking-wider text-[11px] sm:text-xs font-semibold border-2 border-blue-400/70 bg-blue-500/15 text-blue-200 active:scale-95 whitespace-nowrap flex-shrink-0"
            >
              <PlayIcon className="w-4 h-4 inline me-1" /> {t('preview') || 'Reproducir'}
            </button>
          )}
          <button
            onClick={clearAll}
            data-testid="editor-easy-clear"
            className="py-2.5 px-3 rounded-full font-h uppercase tracking-wider text-[11px] sm:text-xs font-semibold border-2 border-gold/40 bg-navy-deep text-gold active:scale-95 whitespace-nowrap flex-shrink-0"
          >
            <Trash2 className="w-4 h-4 inline me-1" /> {t('ee_btn_clear')}
          </button>
          <button
            onClick={save}
            disabled={saving || Object.keys(paths).length === 0}
            data-testid="editor-easy-save"
            title={Object.keys(paths).length === 0 ? t('ee_tooltip_drag_first') : t('ee_tooltip_save_play')}
            className="py-2.5 px-4 rounded-full font-h uppercase tracking-wider text-[11px] sm:text-xs font-bold bg-gold text-navy active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap flex-shrink-0"
          >
            {saving ? '...' : savedId ? <><Check className="w-4 h-4 inline me-1" /> {t('ee_btn_saved')}</> : <><Save className="w-4 h-4 inline me-1" /> {t('ee_btn_save')}</>}
          </button>
        </div>
      )}

      {/* Post-save quick actions */}
      {savedId && (
        <div className="mt-4 bg-card-steel border border-gold/40 rounded-lg p-4 flex flex-wrap items-center justify-between gap-3" data-testid="editor-easy-saved">
          <div className="text-sm text-white">
            <span className="text-gold font-h uppercase tracking-wider me-2">Listo</span>
            Tu jugada está guardada. ¿Qué hacés ahora?
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { setSavedId(null); clearAll(); }}
              className="px-4 py-2 rounded-md border border-gold/40 text-gold hover:bg-gold/10 text-sm font-h uppercase tracking-wider"
              data-testid="editor-easy-new"
            >
              Nueva jugada
            </button>
            <button
              onClick={() => navigate(`/playbook/${savedId}`)}
              className="px-4 py-2 rounded-md bg-gold text-navy hover:bg-gold-light text-sm font-h uppercase tracking-wider font-semibold"
              data-testid="editor-easy-view"
            >
              Ver jugada
            </button>
          </div>
        </div>
      )}

      {/* Contextual action menu — opens above a player on double-tap */}
      {actionMenu && (
        <>
          {/* Backdrop catches outside taps to close */}
          <div
            className="fixed inset-0 z-[80]"
            onClick={closeMenu}
            data-testid="editor-easy-action-backdrop"
          />
          <div
            className="fixed z-[81] bg-navy-deep border-2 border-gold/60 rounded-xl shadow-2xl p-2 w-[280px]"
            style={{
              left: Math.max(12, Math.min(window.innerWidth - 292, actionMenu.screen.x - 140)),
              top:  Math.max(12, actionMenu.screen.y - 320),
            }}
            data-testid="editor-easy-action-menu"
          >
            <div className="flex items-center justify-between px-2 pb-1.5 mb-1.5 border-b border-gold/20">
              <div className="text-[11px] font-mono uppercase tracking-widest text-gold">
                Jugador #{players.find(p => p.id === actionMenu.playerId)?.label}
              </div>
              <button onClick={closeMenu} className="text-gold/70 hover:text-gold" aria-label="Cerrar">
                <XIcon className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {[
                { id: 'pase',     label: 'Pase',        Icon: ArrowRightLeft },
                { id: 'drible',   label: 'Drible',      Icon: Repeat },
                { id: 'corte',    label: 'Corte',       Icon: Move },
                { id: 'pantalla', label: 'Pantalla',    Icon: Shield },
                { id: 'bloqueo',  label: 'Bloqueo',     Icon: Shield },
                { id: 'handoff',  label: 'Handoff',     Icon: Hand },
                { id: 'pickroll', label: 'Pick & Roll', Icon: Target },
                { id: 'pickpop',  label: 'Pick & Pop',  Icon: Target },
                { id: 'tiro',     label: 'Tiro',        Icon: CircleDot },
                { id: 'tiro3',    label: 'Tiro de 3',   Icon: CircleDot },
              ].map(({ id, label, Icon }) => (
                <button
                  key={id}
                  onClick={() => runAction(id)}
                  data-testid={`easy-action-${id}`}
                  className="px-2 py-2 rounded-md border border-steel hover:border-gold hover:bg-gold/10 text-white text-xs font-h uppercase tracking-wider inline-flex items-center gap-1.5 active:scale-95 transition"
                >
                  <Icon className="w-3.5 h-3.5 text-gold/80" />
                  <span className="truncate">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Roster picker modal — Añadir Roster: map slots → real players */}
      {rosterOpen && (
        <>
          <div
            className="fixed inset-0 z-[90] bg-black/70"
            onClick={() => setRosterOpen(false)}
            data-testid="editor-easy-roster-backdrop"
          />
          <div
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[91] bg-navy-deep border-2 border-gold/60 rounded-xl shadow-2xl p-4 w-[92%] max-w-md max-h-[85vh] overflow-y-auto"
            data-testid="editor-easy-roster-modal"
          >
            <div className="flex items-center justify-between mb-3 pb-3 border-b border-gold/20">
              <div>
                <h3 className="font-h uppercase text-lg text-gold">Asignar Roster</h3>
                <div className="text-[11px] text-gray-400 mt-0.5">
                  Reemplazá 1-2-3-4-5 con tus jugadores reales.
                </div>
              </div>
              <button
                onClick={() => setRosterOpen(false)}
                className="text-gold/70 hover:text-gold p-1"
                aria-label="Cerrar"
                data-testid="editor-easy-roster-close"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>
            {!rosterLoaded ? (
              <div className="text-gray-400 text-sm py-4 text-center">Cargando roster…</div>
            ) : rosterList.length === 0 ? (
              <div className="text-gray-400 text-sm py-4">
                No hay jugadores en el roster de <span className="text-gold">{activeTeam?.name || 'tu equipo'}</span>. Andá a la sección <span className="text-gold">Roster</span> y agregá jugadores primero.
                <div className="mt-3 text-center">
                  <button
                    onClick={() => { setRosterOpen(false); navigate('/roster'); }}
                    className="px-4 py-2 rounded-md bg-gold text-navy text-sm font-h uppercase tracking-wider font-semibold"
                    data-testid="editor-easy-goto-roster"
                  >
                    Ir a Roster
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2.5">
                {focalPlayers.map(p => {
                  const stroke = p.team === 'offense' ? GOLD : RED;
                  return (
                    <div key={p.id} className="flex items-center gap-3" data-testid={`editor-easy-roster-row-${p.id}`}>
                      <div
                        className="w-10 h-10 rounded-full border-2 flex-shrink-0 flex items-center justify-center text-sm font-h font-bold"
                        style={{ borderColor: stroke, color: stroke, background: '#0A192F' }}
                      >
                        {p.label}
                      </div>
                      <select
                        value={rosterPicked[p.id] || ''}
                        onChange={e => assignRoster(p.id, e.target.value)}
                        className="flex-1 bg-card-steel border border-steel rounded px-2 py-2 text-sm text-white outline-none focus:border-gold"
                        data-testid={`editor-easy-roster-select-${p.id}`}
                      >
                        <option value="">— Sin asignar —</option>
                        {rosterList.map(rp => {
                          const parts = [rp.name];
                          if (rp.number != null && rp.number !== '') parts.push(`#${rp.number}`);
                          if (rp.position) parts.push(rp.position);
                          return (
                            <option key={rp.id} value={rp.id}>
                              {parts.join(' · ')}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  );
                })}
                <button
                  onClick={() => setRosterOpen(false)}
                  className="w-full mt-3 px-4 py-2.5 rounded-md bg-gold text-navy font-h uppercase tracking-wider text-sm font-bold"
                  data-testid="editor-easy-roster-apply"
                >
                  Aplicar
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );

  return fullscreen ? (
    <div
      data-testid="editor-easy-fullscreen"
      className="fixed inset-0 z-40 bg-navy-deep text-white overflow-hidden flex flex-col px-3 pt-2 pb-2"
      style={{ touchAction: 'pan-y' }}
    >
      {inner}
    </div>
  ) : (
    <Layout>{inner}</Layout>
  );
}
